import { useState } from 'react';
import {
  CategoryMaster,
  PostTypeMaster,
  TypeMaster,
} from '../../../services/apiTypes';
import {
  MANAGE_ITEM_TEMP_ID,
  MANAGE_ITEM_MODE,
} from '../../../_constants/manageConst';
import { isGetTypeMaster } from '../../../_utils/typeGuardUtils';
import { API_CODE } from '../../../_constants/apiConst';
import {
  deleteTypeMaster,
  getTypeMaster,
  postTypeMaster,
} from '../../../services/api/typeMaster';
import {
  MANUAL_CONTAINER_NAME,
  NG_SAMPLE_CONTAINER_NAME,
} from '../../../_constants/inspectionConst';
import { deleteFiles } from '../../../stores/confirmedInspection/api';

const defaultType = {
  categoryId: '',
  typeId: '',
  displayName: '',
  typeName: '',
  typeNameJp: '',
  typeName03: '',
  typeName04: '',
  typeName05: '',
  typeName06: '',
  typeName07: '',
  typeName08: '',
  typeName09: '',
  typeName10: '',
  sort: 0,
  isOptionalAddition: false,
  isVisualInspection: false,
  displayType: 1,
  documentPath: '',
  memo: '',
  typeImageMaster: [],
};

type useTypeProps = {
  categoryMaster: CategoryMaster[];
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  setDisplayMode: React.Dispatch<React.SetStateAction<number>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDisabledControlBtn: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenAccordions: Record<string, boolean>;
  handleAccordion: (id: string) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string) => void;
};

const useType = ({
  categoryMaster,
  typeMaster,
  setTypeMaster,
  setDisplayMode,
  setIsOpenDeleteModal,
  setIsDisabledControlBtn,
  isOpenAccordions,
  handleAccordion,
  setIsLoading,
  showApiError,
  showTypeError,
}: useTypeProps) => {
  /** 操作対象の中項目 */
  const [targetType, setTargetType] = useState<TypeMaster>(defaultType);
  /** 中項目編集モーダルの開閉状態 */
  const [isOpenTypeEditModal, setIsOpenTypeEditModal] = useState(false);

  /** 操作対象中項目をリセットする */
  const resetTargetType = () => {
    setTargetType(defaultType);
  };

  /** 大項目行の新規追加ボタン押下（中項目追加） */
  const onClickCategoryAddIcon = (categoryId: string) => {
    const newType = {
      ...defaultType,
      categoryId,
      typeId: MANAGE_ITEM_TEMP_ID.TYPE,
    };
    setTypeMaster([...typeMaster, newType]);
    setDisplayMode(MANAGE_ITEM_MODE.ADD);
    setTargetType(newType);
    if (!isOpenAccordions[categoryId]) {
      handleAccordion(categoryId);
    }
  };

  /** 中項目の新規追加 */
  const onClickTypeAddSave = async (
    typeName: string,
    displayType: number,
    isOptionalAddition: boolean,
  ) => {
    setIsDisabledControlBtn(true);
    setTargetType({ ...targetType, typeName, displayType, isOptionalAddition });
    const postType: PostTypeMaster = {
      categoryId: targetType.categoryId,
      typeName,
      displayType,
      isOptionalAddition,
    };
    try {
      await postTypeMaster(postType);
      // 中項目再取得
      const response = await getTypeMaster();
      if (!isGetTypeMaster(response)) {
        showTypeError(API_CODE.TYPE_MASTER);
      }
      setTypeMaster(response);
      resetTargetType();
      setDisplayMode(MANAGE_ITEM_MODE.VIEW);
    } catch (error) {
      showApiError(error, API_CODE.TYPE_MASTER);
    }
    setIsDisabledControlBtn(false);
  };

  /** 中項目の新規追加キャンセル */
  const onClickTypeAddCancel = () => {
    setTypeMaster(
      typeMaster.filter((t) => t.typeId !== MANAGE_ITEM_TEMP_ID.TYPE),
    );
    resetTargetType();
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
  };

  /** 中項目の編集アイコン押下 */
  const onClickTypeEditIcon = async (type: TypeMaster) => {
    setTargetType(type);
    setIsOpenTypeEditModal(true);
  };

  /** アップロード用のパス作成 */
  const getUploadPath = (
    categoryId: string,
    typeId: string,
    fileName: string,
  ) => {
    const category = categoryMaster.find((c) => c.categoryId === categoryId);
    if (!category) {
      return '';
    }
    // fileNameが空の場合は中項目までのファイルパスを返す
    return (
      category.categoryId + '/' + typeId + (fileName ? '/' + fileName : '')
    );
  };

  /** 中項目の削除アイコン押下 */
  const onClickTypeDeleteIcon = (type: TypeMaster) => {
    setTargetType(type);
    setIsOpenDeleteModal(true);
  };

  /** 削除処理実行 */
  const executeDeleteType = async () => {
    setIsOpenDeleteModal(false);
    setIsLoading(true);
    try {
      // マニュアルとNGサンプルを削除する
      const deletePath = getUploadPath(
        targetType.categoryId,
        targetType.typeId,
        '',
      );
      await deleteFiles(MANUAL_CONTAINER_NAME, deletePath);
      await deleteFiles(NG_SAMPLE_CONTAINER_NAME, deletePath);
    } catch (error) {
      showApiError(error, API_CODE.DELETE_FILES);
      resetTargetType();
      setIsLoading(false);
      return;
    }

    try {
      // 中項目削除
      await deleteTypeMaster({ typeId: targetType.typeId });
      // 画面を更新
      const response = await getTypeMaster();
      if (!isGetTypeMaster(response)) {
        showTypeError(API_CODE.TYPE_MASTER);
      } else {
        setTypeMaster(response);
      }
    } catch (error) {
      showApiError(error, API_CODE.TYPE_MASTER);
    } finally {
      resetTargetType();
      setIsLoading(false);
    }
  };

  /** 中項目の編集保存 */
  const handleTypeSave = async () => {
    try {
      setIsOpenTypeEditModal(false);
      resetTargetType();
      setDisplayMode(MANAGE_ITEM_MODE.VIEW);
      // displayNameを更新するため再取得
      const response = await getTypeMaster();
      if (!isGetTypeMaster(response)) {
        showTypeError(API_CODE.TYPE_MASTER);
      }
      setTypeMaster(response);
    } catch (error) {
      showApiError(error, API_CODE.TYPE_MASTER);
    }
  };

  /** 中項目の編集キャンセル */
  const handleTypeCancel = () => {
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
    setIsOpenTypeEditModal(false);
    resetTargetType();
  };

  return {
    targetType,
    isOpenTypeEditModal,
    resetTargetType,
    onClickCategoryAddIcon,
    onClickTypeAddSave,
    onClickTypeAddCancel,
    onClickTypeEditIcon,
    getUploadPath,
    onClickTypeDeleteIcon,
    executeDeleteType,
    handleTypeSave,
    handleTypeCancel,
  };
};

export default useType;
